import type { EcommerceItem } from './types';

/**
 * Browser-lifetime state the GA4 funnel needs but no component can own.
 *
 * Three facts outlive the components that produce them:
 *
 * - which trips have already been reported as impressions for a search, so a
 *   remount of the results list does not report them again (the list is
 *   unmounted by `SlideInOut unmountOnExit` on every direction change, and
 *   remounts around hydration on a fast load);
 * - what is in the cart for each leg, so replacing a trip can report the
 *   removal even after the trips store has been cleared by navigating back to
 *   the results — which is exactly what the Departure breadcrumb does;
 * - which searches have already fired `search`, so a refresh or retry of the
 *   same search does not fire it again.
 *
 * Module-level rather than Redux, following the enrichment-plugin precedent
 * (see `.docs/knowledge/analytics/enrichment-plugins.md`): these are analytics
 * bookkeeping, not application state, and nothing renders from them.
 *
 * Browser-only. The server bundle imports this module too, and module state
 * there is shared by every request in the process, so a write from a server
 * render would leak one traveler's cart into another's. Nothing in the funnel
 * runs during SSR, and the guard keeps it that way.
 */

interface ReportedImpressions {
    searchKey: string;
    tripIds: Set<string>;
}

// One entry per leg. Depart and return are separate lists that can both be
// live, and replacing an entry when its search key changes keeps this bounded.
const reportedImpressions = new Map<string, ReportedImpressions>();

const cartItems = new Map<string, EcommerceItem>();

// Every search this page has reported, so the `search` event fires once per
// search however many times the results re-render, remount, refresh their
// expired trips or re-fetch after an error. Grows by one per distinct search
// the traveler runs, which a single page lifetime keeps small.
const reportedSearches = new Set<string>();

// An entry point only the click that caused the search knows (a date
// suggestion), held for the search it names until that search is claimed.
let pendingSearchEntryPoint: { entryPoint: string; searchKey: string } | undefined;

function isBrowser(): boolean {
    return typeof window !== 'undefined';
}

/**
 * The trips in `tripIds` that have not been reported for this search yet,
 * marking them reported. A new `searchKey` starts the leg over.
 */
function takeUnreportedTrips({
    legKey,
    searchKey,
    tripIds,
}: {
    legKey: string;
    searchKey: string;
    tripIds: string[];
}): string[] {
    if (!isBrowser()) {
        return tripIds;
    }

    const existing = reportedImpressions.get(legKey);
    const reported = existing && existing.searchKey === searchKey
        ? existing.tripIds
        : new Set<string>();

    reportedImpressions.set(legKey, { searchKey, tripIds: reported });

    const unreported = tripIds.filter((tripId) => !reported.has(tripId));

    unreported.forEach((tripId) => reported.add(tripId));

    return unreported;
}

function rememberCartItem(legKey: string, item: EcommerceItem): void {
    if (!isBrowser()) {
        return;
    }

    cartItems.set(legKey, item);
}

/** The leg's trip has left the cart -- rejected, or the itinerary abandoned. */
function forgetCartItem(legKey: string): void {
    cartItems.delete(legKey);
}

function getCartItem(legKey: string): EcommerceItem | undefined {
    return isBrowser()
        ? cartItems.get(legKey)
        : undefined;
}

function isSearchReported(searchKey: string): boolean {
    return reportedSearches.has(searchKey);
}

function setPendingSearchEntryPoint(pending: { entryPoint: string; searchKey: string }): void {
    if (!isBrowser()) {
        return;
    }

    pendingSearchEntryPoint = pending;
}

/**
 * Marks `searchKey` reported. Nothing when it already was; otherwise whether it
 * is the first search this page has reported, and the pending entry point if it
 * was set for this search.
 *
 * Any claim clears the pending entry point: one set for a search that never
 * claims (its date had already been searched) must not label the next search
 * the traveler runs.
 */
function claimSearch(
    searchKey: string,
): { entryPoint?: string; isFirstSearch: boolean } | undefined {
    if (!isBrowser() || reportedSearches.has(searchKey)) {
        return undefined;
    }

    const isFirstSearch = reportedSearches.size === 0;
    const pending = pendingSearchEntryPoint;

    reportedSearches.add(searchKey);
    pendingSearchEntryPoint = undefined;

    return pending && pending.searchKey === searchKey
        ? { entryPoint: pending.entryPoint, isFirstSearch }
        : { isFirstSearch };
}

function resetSessionState(): void {
    reportedImpressions.clear();
    cartItems.clear();
    reportedSearches.clear();
    pendingSearchEntryPoint = undefined;
}

export {
    claimSearch,
    forgetCartItem,
    getCartItem,
    isSearchReported,
    rememberCartItem,
    resetSessionState,
    setPendingSearchEntryPoint,
    takeUnreportedTrips,
};
